/*
 * The ONE composition statement (E02.F09 — R114, R117, feature AC-7583b31d).
 * UI-free (no JSX): `lib` holds no domain UI, only the data and helpers the
 * surface reads.
 *
 * There is exactly ONE statement of what each kind of organization is composed
 * of, and every page is built from it (R117 AC-8d7f30c1 / AC-7fd36967). Change a
 * kind's set here and every page of that kind changes (AC-0651c548). No route
 * carries its own copy of a kind's section list — that is the drift this file
 * exists to make impossible (feature AC-7583b31d: two routes to the same
 * organization can never disagree about what it is composed of).
 *
 * Each kind is composed of its OWN named set, differing kind to kind
 * (R114 AC-dd69078b). The entity KIND (Identity's `core.entity_type`, surfaced
 * by the BFF — Client, Agency, Provider, Software) is the key; the resource
 * routes (`/providers`, `/tpas`, …) are a presentation detail layered over the
 * same four kinds, not a fifth source of truth.
 *
 * `profile` is every kind's first section and the fallback target: opening an
 * organization lands on its profile (R119), and an address naming a section a
 * kind lacks falls back to the profile rather than failing (R121). The fallback
 * mechanics live in `lib/entity-sections` (`resolveSection`); this file supplies
 * the per-kind set it resolves against.
 *
 * The "lead to content" guarantee (R114 AC-1f3f04ae) is that every section id
 * here is rendered by the entity shell's section body — no section is a dead
 * tab. The shell renders a body for every id in these sets (a placeholder where
 * a feature's data contract is not yet built is still content, not a 404); a
 * build-time/test assertion (`entity-composition.test.ts`) proves every section
 * resolves to a rendered body so a section can never be added here without a
 * body behind it.
 */
import type { EntityKind } from '@/lib/api/domains/entities';
import type { EntitySection } from '@/lib/entity-sections';

/*
 * Client set (R114 AC-aaf7c99e): profile, members, billing transactions,
 * billing statements, cash receivables, plans, brokers, bank accounts. The
 * profile itself is composed of addresses, groups/divisions, salary & tobacco
 * configuration and users (R118) — composed inside the profile body, not as
 * sibling sections.
 */
const CLIENT_SECTIONS: readonly EntitySection[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'members', label: 'Members' },
  { id: 'billing-transactions', label: 'Billing Transactions' },
  { id: 'billing-statements', label: 'Billing Statements' },
  { id: 'cash-receivables', label: 'Cash Receivables' },
  { id: 'plans', label: 'Plans' },
  { id: 'brokers', label: 'Brokers' },
  { id: 'bank-accounts', label: 'Bank Accounts' },
];

/*
 * Provider set (R114 AC-1fd4a90e): profile, disbursements, distribution rules,
 * contracts, reconciliation, bank accounts, disbursement schedules. A provider
 * (carrier) has Reconciliation and Disbursements where a client has Cash
 * Receivables — the two sets differ, which is the whole of R114.
 */
const PROVIDER_SECTIONS: readonly EntitySection[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'disbursements', label: 'Disbursements' },
  { id: 'distribution-rules', label: 'Distribution Rules' },
  { id: 'contracts', label: 'Contracts' },
  { id: 'reconciliation', label: 'Reconciliation' },
  { id: 'bank-accounts', label: 'Bank Accounts' },
  { id: 'disbursement-schedules', label: 'Disbursement Schedules' },
];

/*
 * Agency set. An Agency is a producing corporate entity (glossary); a Broker is
 * the Agency kind in a legal form (R35, R126 — "Broker (mapped to a producing-
 * agency designation)"), so both are composed from this one Agency set rather
 * than a separate Broker key. Licenses, commissions, contracts and bank
 * accounts are what a producing agency carries.
 */
const AGENCY_SECTIONS: readonly EntitySection[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'licenses', label: 'Licenses' },
  { id: 'commissions', label: 'Commissions' },
  { id: 'contracts', label: 'Contracts' },
  { id: 'bank-accounts', label: 'Bank Accounts' },
];

/*
 * Software set — Kelly's own organization (the one and only `Software` entity,
 * R126 AC-e3fc9295). It carries its own record (profile) and bank accounts; it
 * is never a client or carrier, so it offers neither a client's nor a
 * provider's money sections. It is composed from the one statement like every
 * other kind, so a route can never give Kelly a section the statement does not.
 */
const SOFTWARE_SECTIONS: readonly EntitySection[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'bank-accounts', label: 'Bank Accounts' },
];

/*
 * THE composition statement. One entry per `core.entity_type` value the BFF can
 * return (Client, Agency, Provider, Software — Verified, V048). There is NO
 * `TPA` key: `core.entity_type` has no `TPA` value (Verified absent, V048), so a
 * fifth `TPA` kind is neither composed nor shown until CR-E02-08 ships
 * (R126 AC-40d18696 / AC-7256bf27, BLOCKED). This record is `satisfies` the full
 * `EntityKind` union, so adding a kind to the enum without composing it here is
 * a compile error — the single-source guarantee enforced by the type system.
 */
export const ENTITY_COMPOSITION = {
  Client: CLIENT_SECTIONS,
  Provider: PROVIDER_SECTIONS,
  Agency: AGENCY_SECTIONS,
  Software: SOFTWARE_SECTIONS,
} satisfies Record<EntityKind, readonly EntitySection[]>;

/** The ordered named section set for a kind — the one read every page makes. */
export function sectionsForKind(kind: EntityKind): readonly EntitySection[] {
  return ENTITY_COMPOSITION[kind];
}

/*
 * The decided creatable kinds offered on the create pick-list (R126). FOUR
 * kinds: Client, Agency, Broker (the Agency kind in a legal form — a producing-
 * agency designation, R35/R126), and Provider (a carrier; a partner platform is
 * recorded as a Provider, AC-50446af0). There is NO path to a second `Software`
 * entity (AC-e3fc9295): `Software` is Kelly's own record and is not an offerable
 * create kind. There is NO fifth `TPA` kind (AC-40d18696, BLOCKED — CR-E02-08).
 *
 * `value` is the `core.entity_type` the create writes; `Broker` presents as a
 * label over the `Agency` value, so the list reads as four kinds while writing
 * only the enum values the platform holds.
 */
export interface CreatableKind {
  /** The `core.entity_type` value this choice creates. */
  value: EntityKind;
  /** The label shown in the pick-list. */
  label: string;
}

export const CREATABLE_KINDS: readonly CreatableKind[] = [
  { value: 'Client', label: 'Client' },
  { value: 'Agency', label: 'Agency' },
  { value: 'Agency', label: 'Broker' },
  { value: 'Provider', label: 'Provider (carrier)' },
];
